import { PackProgramWithContext } from '@rhg/utils/pack-program/types';
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

interface DisplayedCarouselProgramProps {
    displayedItems?: PackProgramWithContext[];
    isFetched?: boolean;
    isMHRetriggerShown?: boolean;
}
export interface DisplayedCarouselProgramContextValues {
    setDisplayedItemIndex: (index: number) => void;
    addVisibleItem: (index: number) => void;
    removeVisibleItem: (index: number) => void;
    displayedItemIndex?: number;
    visibleItemsIndex?: number[];
    displayedItem?: PackProgramWithContext;
    visibleItems?: PackProgramWithContext[];
}

export const DisplayedCarouselProgramContext =
    createContext<DisplayedCarouselProgramContextValues>({
        setDisplayedItemIndex: () => null,
        addVisibleItem: () => null,
        removeVisibleItem: () => null,
        displayedItemIndex: 0,
        visibleItemsIndex: [],
    });

export function DisplayedCarouselProgramProvider({
    displayedItems,
    isFetched,
    isMHRetriggerShown,
    children,
}: PropsWithChildren & DisplayedCarouselProgramProps) {
    const [displayedItem, setDisplayedItem] =
        useState<PackProgramWithContext>();
    const [visibleItems, setVisibleItems] = useState<PackProgramWithContext[]>(
        []
    );
    // The index is needed since some carousels manually add the "view all clinical programs" that is not part of the displayedItems array. This leads to the displayedItem not changing and doesn't trigger the change to the consumer of this context. The index state change will allow the change to be detected.
    const [displayedItemIndex, setDisplayedItemIndex] = useState<number>();
    const [visibleItemsIndex, setVisibleItemsIndex] = useState<number[]>([]);

    const setDisplayedItemIndexIntercept = useCallback(
        (index: number) => {
            setDisplayedItemIndex(index);
            if (isFetched && !!displayedItems?.length) {
                setDisplayedItem(
                    displayedItems[isMHRetriggerShown ? index - 1 : index]
                );
            }
        },
        [isFetched, displayedItems, isMHRetriggerShown]
    );

    const addVisibleItem = useCallback(
        (index: number) => {
            setVisibleItemsIndex((visibleItemsIndex) => [
                ...visibleItemsIndex,
                index,
            ]);
            if (isFetched && !!visibleItems?.length) {
                setVisibleItems((visibleItems) => [
                    ...visibleItems,
                    visibleItems[isMHRetriggerShown ? index - 1 : index],
                ]);
            }
        },
        [isFetched, visibleItems?.length, isMHRetriggerShown]
    );
    const removeVisibleItem = useCallback(
        (index: number) => {
            setVisibleItemsIndex((visibleItemsIndex) =>
                visibleItemsIndex.filter((i) => i !== index)
            );
            if (isFetched && !!visibleItems?.length) {
                setVisibleItems((visibleItems) =>
                    visibleItems.filter((_, itemIndex) => itemIndex !== index)
                );
            }
        },
        [isFetched, visibleItems?.length]
    );

    useEffect(() => {
        if (
            isFetched &&
            displayedItems &&
            !displayedItem &&
            !isMHRetriggerShown
        ) {
            setDisplayedItem(displayedItems[0]);
        }
    }, [isFetched, displayedItems, displayedItem, isMHRetriggerShown]);

    const state = useMemo(
        () => ({
            displayedItem,
            setDisplayedItemIndex: setDisplayedItemIndexIntercept,
            displayedItemIndex,
            addVisibleItem,
            removeVisibleItem,
            visibleItems,
            visibleItemsIndex,
        }),
        [
            displayedItem,
            setDisplayedItemIndexIntercept,
            displayedItemIndex,
            removeVisibleItem,
            addVisibleItem,
            visibleItems,
            visibleItemsIndex,
        ]
    );

    return (
        <DisplayedCarouselProgramContext.Provider value={state}>
            {children}
        </DisplayedCarouselProgramContext.Provider>
    );
}
